import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import FinalReportAreaCard from '../components/FinalReportAreaCard';
import FinalReportDelivery from '../components/FinalReportDelivery';
import { assessmentsService, FinalReportAreaEntry } from '../api/assessmentsService';
import { saveStateLabel, useDebouncedAutosave } from '../hooks/useDebouncedAutosave';
import { getImageUrl } from '../utils/imageUrl';

type RecommendationValue = 'red' | 'yellow' | 'green';

interface FinalReportFields {
  finalRecommendation: RecommendationValue | '';
  growthRecommendations: string;
  mbcbRecommendations: string;
  discussionMinutes: string;
}

const recommendationOptions: Array<{ value: RecommendationValue; label: string; className: string }> = [
  { value: 'red', label: 'Red', className: 'text-red-700' },
  { value: 'yellow', label: 'Yellow', className: 'text-yellow-700' },
  { value: 'green', label: 'Green', className: 'text-green-700' },
];

const ratingDotClass: Record<RecommendationValue, string> = {
  red: 'bg-red-500',
  yellow: 'bg-yellow-400',
  green: 'bg-green-500',
};

const areaStatusChipClass = (status: FinalReportAreaEntry['reportStatus']) => {
  if (status === 'submitted' || status === 'approved' || status === 'reviewed') return 'bg-green-100 text-green-800';
  if (status === 'draft' || status === 'returned') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-500';
};

const areaStatusLabel = (status: FinalReportAreaEntry['reportStatus']) => {
  if (!status) return 'Not started';
  if (status === 'returned') return 'Returned';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const isAreaComplete = (entry: FinalReportAreaEntry) => (
  entry.reportStatus === 'submitted' || entry.reportStatus === 'approved' || entry.reportStatus === 'reviewed'
);

const RecommendationSelector: React.FC<{
  label: string;
  name: string;
  value: RecommendationValue | '';
  onChange: (value: RecommendationValue | '') => void;
  disabled?: boolean;
}> = ({ label, name, value, onChange, disabled }) => (
  <div className="space-y-2">
    <div className="text-sm font-medium text-gray-700">{label}</div>
    <div className="flex flex-wrap gap-3">
      {recommendationOptions.map((option) => (
        <label
          key={option.value}
          className={[
            'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
            value === option.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200',
            disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            disabled={disabled}
          />
          <span className={option.className}>{option.label}</span>
        </label>
      ))}
      <button
        type="button"
        className="text-xs text-gray-500 underline underline-offset-2"
        onClick={() => onChange('')}
        disabled={disabled}
      >
        Clear
      </button>
    </div>
  </div>
);

const AssessmentFinalReport: React.FC = () => {
  const { id, participantId } = useParams();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const participantIdNum = useMemo(() => parseInt(participantId || '0', 10), [participantId]);
  const queryClient = useQueryClient();

  const finalReportQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'final-reports', participantIdNum],
    queryFn: () => assessmentsService.getFinalReport(assessmentId, participantIdNum),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0 && Number.isFinite(participantIdNum) && participantIdNum > 0,
  });
  const assessmentQuery = useQuery({
    queryKey: ['assessments', assessmentId],
    queryFn: () => assessmentsService.get(assessmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0,
  });

  const [fields, setFields] = useState<FinalReportFields>({
    finalRecommendation: '',
    growthRecommendations: '',
    mbcbRecommendations: '',
    discussionMinutes: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showInternal, setShowInternal] = useState(false);

  const hydratedForRef = useRef<number | null>(null);

  // Hydrate once per participant so a refetch triggered by autosave cannot
  // overwrite typing, while switching candidates still reloads the form.
  useEffect(() => {
    const finalReport = finalReportQuery.data?.data?.finalReport;
    if (!finalReportQuery.data || hydratedForRef.current === participantIdNum) return;
    setFields({
      finalRecommendation: (finalReport?.finalRecommendation as RecommendationValue) || '',
      growthRecommendations: finalReport?.growthRecommendations || '',
      mbcbRecommendations: finalReport?.mbcbRecommendations || '',
      discussionMinutes: Number.isFinite(finalReport?.finalRecommendationDiscussionMinutes)
        ? String(finalReport?.finalRecommendationDiscussionMinutes)
        : '',
    });
    hydratedForRef.current = participantIdNum;
  }, [finalReportQuery.data, participantIdNum]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['assessments', assessmentId, 'final-reports', participantIdNum],
    });
  }, [queryClient, assessmentId, participantIdNum]);

  const saveMutation = useMutation({
    mutationFn: (payload: Parameters<typeof assessmentsService.saveFinalReport>[2]) =>
      assessmentsService.saveFinalReport(assessmentId, participantIdNum, payload),
    onSuccess: () => {
      invalidate();
      setErrorMessage(null);
    },
    onError: (err: any) => {
      setErrorMessage(err?.response?.data?.message || 'Failed to save final report.');
    },
  });

  const data = finalReportQuery.data?.data;
  const canEdit = data?.canEdit || false;

  const toPayload = useCallback((values: FinalReportFields) => {
    const minutes = values.discussionMinutes.trim();
    return {
      finalRecommendation: values.finalRecommendation || null,
      growthRecommendations: values.growthRecommendations.trim() || null,
      mbcbRecommendations: values.mbcbRecommendations.trim() || null,
      finalRecommendationDiscussionMinutes: minutes ? Number(minutes) : null,
    };
  }, []);

  const { saveState, setSaveState, schedule, cancel } = useDebouncedAutosave<FinalReportFields>({
    enabled: canEdit,
    save: (values) => saveMutation.mutateAsync(toPayload(values)),
  });

  // Queued edits target whichever participant is current when they flush, so
  // drop them rather than writing one candidate's text onto another.
  useEffect(() => cancel, [participantIdNum, cancel]);

  // Next value is computed outside the state updater: React invokes updaters
  // twice in StrictMode, which would queue the autosave twice.
  const updateField = <K extends keyof FinalReportFields>(key: K, value: FinalReportFields[K]) => {
    const next = { ...fields, [key]: value };
    setFields(next);
    schedule(next);
  };

  const saveNow = () => {
    cancel();
    setSaveState('saving');
    saveMutation.mutate(toPayload(fields), {
      onSuccess: () => setSaveState('saved'),
      onError: () => setSaveState('error'),
    });
  };

  const participant = data?.participant;
  const portalCandidate = participant?.portalCandidate;
  const candidateName = portalCandidate?.name || 'Candidate';
  const spouseName = portalCandidate?.spouseName || '';
  const candidateLabel = spouseName ? `${candidateName} & ${spouseName}` : candidateName;
  const callingPhotoUrl = getImageUrl(data?.finalReport?.callingPhotoImagePath, { width: 900, quality: 70, format: 'webp' });
  const discussionPhotoUrl = getImageUrl(data?.finalReport?.discussionPhotoImagePath, { width: 900, quality: 70, format: 'webp' });
  const publishedAt = data?.finalReport?.publishedToCandidateAt || null;

  const areaReports = data?.areaReports || [];
  const completedAreas = areaReports.filter(isAreaComplete).length;
  const editedAreas = areaReports.filter((entry) => entry.isEdited).length;

  const uploadMutation = useMutation({
    mutationFn: (file: File) => assessmentsService.uploadFinalReportCallingPhoto(assessmentId, participantIdNum, file),
    onSuccess: () => {
      invalidate();
      setErrorMessage(null);
    },
    onError: (err: any) => {
      setErrorMessage(err?.response?.data?.message || 'Failed to upload calling photo.');
    },
  });

  const handleCallingPhotoUpload = async (file: File | null) => {
    if (!file || uploadMutation.isPending) return;
    await uploadMutation.mutateAsync(file);
  };

  const discussionUploadMutation = useMutation({
    mutationFn: (file: File) => assessmentsService.uploadFinalReportDiscussionPhoto(assessmentId, participantIdNum, file),
    onSuccess: () => {
      invalidate();
      setErrorMessage(null);
    },
    onError: (err: any) => {
      setErrorMessage(err?.response?.data?.message || 'Failed to upload discussion photo.');
    },
  });

  const handleDiscussionPhotoUpload = async (file: File | null) => {
    if (!file || discussionUploadMutation.isPending) return;
    await discussionUploadMutation.mutateAsync(file);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Final Report</h1>
              <p className="text-gray-600 mt-1 flex items-center gap-2">
                {candidateLabel}
                {assessmentQuery.data?.data?.isDoubleAssessment && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">Team {participant?.teamKey || 'A'}</span>
                )}
              </p>
            </div>
            <Link to={`/assessments/${assessmentId}/after-party`} className="text-sm text-primary-600 hover:text-primary-700">
              Back to After Party
            </Link>
          </div>
        </div>

        {finalReportQuery.isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading final report…</div>
        )}

        {finalReportQuery.isError && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">Failed to load final report.</div>
        )}

        {!finalReportQuery.isLoading && !finalReportQuery.isError && data && (
          <div className="space-y-6">
            <div className="sticky top-0 z-10 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur sm:px-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">
                    Area reports: {completedAreas} of {areaReports.length} submitted
                  </span>
                  {editedAreas > 0 && (
                    <span className="text-xs text-gray-600">{editedAreas} edited for delivery</span>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {areaReports.map((entry) => (
                      <span
                        key={entry.roleId}
                        className={['rounded-full px-2 py-0.5 text-[11px] font-medium', areaStatusChipClass(entry.reportStatus)].join(' ')}
                        title={`${entry.roleName}: ${areaStatusLabel(entry.reportStatus)}`}
                      >
                        {entry.roleName}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {publishedAt && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                      Published to candidate
                    </span>
                  )}
                  {fields.finalRecommendation && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className={['inline-flex h-2.5 w-2.5 rounded-full', ratingDotClass[fields.finalRecommendation]].join(' ')} />
                      Final: {fields.finalRecommendation}
                    </span>
                  )}
                  {canEdit && (
                    <span className={['text-xs', saveState === 'error' ? 'text-red-700' : 'text-gray-500'].join(' ')}>
                      {saveState === 'error' ? 'Could not save' : saveStateLabel[saveState]}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errorMessage}
              </div>
            )}

            {/* Area reports on the left, recommendations on the right, so the
                writer can draft while the team talks through the report. */}
            <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
              <div className="space-y-4 rounded-xl bg-white p-4 shadow-lg sm:p-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Area Reports</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    The text here is what the candidate receives. The assessors' own reports are kept
                    as written and are never changed by edits on this page.
                  </p>
                </div>
                <div className="space-y-3">
                  {areaReports.map((entry) => (
                    <FinalReportAreaCard
                      key={entry.roleId}
                      assessmentId={assessmentId}
                      participantId={participantIdNum}
                      entry={entry}
                      canEdit={canEdit}
                      onSectionReset={invalidate}
                      aiAssist={data.aiAssist}
                    />
                  ))}
                  {areaReports.length === 0 && (
                    <div className="text-sm text-gray-600">No area reports available yet.</div>
                  )}
                </div>
              </div>

              <div className="space-y-6 lg:sticky lg:top-20">
                <div className="space-y-6 rounded-xl bg-white p-4 shadow-lg sm:p-6">
                  <h2 className="text-lg font-semibold text-gray-900">Recommendations</h2>

                  <RecommendationSelector
                    label="Final recommendation"
                    name="finalRecommendation"
                    value={fields.finalRecommendation}
                    onChange={(value) => updateField('finalRecommendation', value)}
                    disabled={!canEdit}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Growth recommendations</label>
                    <textarea
                      className="w-full min-h-[260px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                      value={fields.growthRecommendations}
                      onChange={(e) => updateField('growthRecommendations', e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>

                  {(canEdit || fields.mbcbRecommendations.trim()) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">MBCB recommendations</label>
                      <textarea
                        className="w-full min-h-[160px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                        value={fields.mbcbRecommendations}
                        onChange={(e) => updateField('mbcbRecommendations', e.target.value)}
                        disabled={!canEdit}
                      />
                      <p className="mt-1 text-xs text-gray-500">Included in the report the candidate receives.</p>
                    </div>
                  )}

                  {canEdit && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                      <span className="text-xs text-gray-500">Changes save automatically as you type.</span>
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        onClick={saveNow}
                        disabled={saveMutation.isPending}
                      >
                        Save now
                      </button>
                    </div>
                  )}
                </div>

                <div className="rounded-xl bg-white p-4 shadow-lg sm:p-6">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left"
                    onClick={() => setShowInternal((prev) => !prev)}
                  >
                    <span>
                      <span className="text-sm font-semibold text-gray-900">Internal notes</span>
                      <span className="ml-2 text-xs text-gray-500">never delivered</span>
                    </span>
                    <span className="text-xs text-primary-600">{showInternal ? 'Hide' : 'Show'}</span>
                  </button>

                  {showInternal && (
                    <div className="mt-4 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discussion time (minutes)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                          value={fields.discussionMinutes}
                          onChange={(e) => updateField('discussionMinutes', e.target.value)}
                          disabled={!canEdit}
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700">Calling photo</div>
                        {callingPhotoUrl && (
                          <img
                            src={callingPhotoUrl}
                            alt="Calling drawing"
                            className="w-full max-h-80 rounded-md border border-gray-200 object-contain bg-gray-50"
                          />
                        )}
                        {canEdit && (
                          <div>
                            <input
                              id="calling-photo-upload"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleCallingPhotoUpload(e.target.files?.[0] || null)}
                              disabled={uploadMutation.isPending}
                            />
                            <label
                              htmlFor="calling-photo-upload"
                              className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              {callingPhotoUrl ? 'Change calling photo' : 'Upload calling photo'}
                            </label>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700">Discussion photo</div>
                        {discussionPhotoUrl && (
                          <img
                            src={discussionPhotoUrl}
                            alt="Discussion notes"
                            className="w-full max-h-80 rounded-md border border-gray-200 object-contain bg-gray-50"
                          />
                        )}
                        {canEdit && (
                          <div>
                            <input
                              id="discussion-photo-upload"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleDiscussionPhotoUpload(e.target.files?.[0] || null)}
                              disabled={discussionUploadMutation.isPending}
                            />
                            <label
                              htmlFor="discussion-photo-upload"
                              className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              {discussionPhotoUrl ? 'Change discussion photo' : 'Upload discussion photo'}
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <FinalReportDelivery
              assessmentId={assessmentId}
              participantId={participantIdNum}
              candidateLabel={candidateLabel}
              publishedAt={publishedAt}
              deliveries={data.deliveries || []}
              canEdit={canEdit}
              onChanged={invalidate}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AssessmentFinalReport;
